import { createDuoSurface, DUO_DISPLAY } from '@motionpitch/template-contract';
import type { Point, ScreenWarpMode } from '../types';
import { createCanvas } from './loadImage';
import { isCanvasMemoryScopeActive, releaseScopedCanvas, retainScopedCanvas } from './canvasMemory';
import { drawTexturedTriangle, prepareSource } from './warp';

type Mesh = { positions: Float32Array; uv: Float32Array; indices: Uint16Array; points: Point[] };
let cachedMesh: { key: string; mesh: Mesh } | null = null;
let gpu: ReturnType<typeof createGpu> | null = null;
let gpuUnavailable = false;

function createMesh(left: Point[], right: Point[], columns: number, rows: number): Mesh {
  const key = [columns, rows, ...left.flatMap(p => [p.x, p.y]), ...right.flatMap(p => [p.x, p.y])].join(',');
  if (cachedMesh?.key === key) return cachedMesh.mesh;
  const surface = createDuoSurface(left, right);
  const a = DUO_DISPLAY.panelWidth / DUO_DISPLAY.innerWidth;
  const b = (DUO_DISPLAY.panelWidth + DUO_DISPLAY.hingeWidth) / DUO_DISPLAY.innerWidth;
  const us = [0];
  for (const [start, end, count] of [[0, a, columns], [a, b, Math.max(8, Math.ceil(columns / 2))], [b, 1, columns]]) {
    for (let i = 1; i <= count; i++) us.push(start + (end - start) * i / count);
  }
  const positions: number[] = [], uv: number[] = [], indices: number[] = [], points: Point[] = [];
  for (let y = 0; y <= rows; y++) {
    for (const u of us) {
      const p = surface(u, y / rows);
      positions.push(p.x, p.y); uv.push(u, y / rows); points.push(p);
    }
  }
  for (let y = 0; y < rows; y++) for (let x = 0; x < us.length - 1; x++) {
    const i = y * us.length + x, j = i + us.length;
    indices.push(i, i + 1, j + 1, i, j + 1, j);
  }
  const mesh = { positions: new Float32Array(positions), uv: new Float32Array(uv), indices: new Uint16Array(indices), points };
  cachedMesh = { key, mesh };
  return mesh;
}

function createGpu() {
  const canvas = createCanvas(1, 1);
  const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true, preserveDrawingBuffer: true });
  if (!gl) return null;
  const shader = (type: number, text: string) => {
    const item = gl.createShader(type);
    if (!item) return null;
    gl.shaderSource(item, text); gl.compileShader(item);
    if (!gl.getShaderParameter(item, gl.COMPILE_STATUS)) { gl.deleteShader(item); return null; }
    return item;
  };
  const vertex = shader(gl.VERTEX_SHADER, `
    attribute vec2 a_position; attribute vec2 a_uv; uniform vec2 u_size; varying vec2 v_uv;
    void main() { gl_Position = vec4(a_position.x / u_size.x * 2.0 - 1.0, 1.0 - a_position.y / u_size.y * 2.0, 0.0, 1.0); v_uv = a_uv; }
  `);
  const fragment = shader(gl.FRAGMENT_SHADER, `
    precision highp float; varying vec2 v_uv; uniform sampler2D u_source;
    void main() { gl_FragColor = texture2D(u_source, v_uv); }
  `);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
  gl.deleteShader(vertex); gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  const position = gl.createBuffer(), uv = gl.createBuffer(), indices = gl.createBuffer(), texture = gl.createTexture();
  if (!position || !uv || !indices || !texture) return null;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  retainScopedCanvas(canvas);
  return { canvas, gl, program, position, uv, indices, texture,
    aPosition: gl.getAttribLocation(program, 'a_position'), aUv: gl.getAttribLocation(program, 'a_uv'),
    size: gl.getUniformLocation(program, 'u_size'), source: gl.getUniformLocation(program, 'u_source'), mesh: null as Mesh | null };
}

/** One mesh/texture for the whole display: no independently cropped halves and
 * no overlapping triangle seams on the normal GPU path. */
export function warpImageToDuo(ctx: CanvasRenderingContext2D, image: CanvasImageSource, left: Point[], right: Point[], options: {
  gridX?: number; gridY?: number; fitMode?: ScreenWarpMode; forceCanvas?: boolean;
} = {}): void {
  const mesh = createMesh(left, right, Math.min(64, Math.max(4, Math.round(options.gridX ?? 30))), Math.min(64, Math.max(4, Math.round(options.gridY ?? 30))));
  const source = prepareSource(image, DUO_DISPLAY.innerWidth / DUO_DISPLAY.innerHeight,
    options.fitMode === 'fit' || options.fitMode === 'contain' ? 'fit' : 'crop', Math.max(ctx.canvas.width, ctx.canvas.height));
  if (!options.forceCanvas) {
    if (gpu?.gl.isContextLost()) gpu = null;
    if (!gpu && !gpuUnavailable) {
      gpu = createGpu();
      gpuUnavailable = !gpu;
    }
    if (gpu) {
      const { gl } = gpu;
      if (gpu.canvas.width !== ctx.canvas.width) gpu.canvas.width = ctx.canvas.width;
      if (gpu.canvas.height !== ctx.canvas.height) gpu.canvas.height = ctx.canvas.height;
      gl.viewport(0, 0, gpu.canvas.width, gpu.canvas.height);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(gpu.program);
      for (const [buffer, location, data] of [[gpu.position, gpu.aPosition, mesh.positions], [gpu.uv, gpu.aUv, mesh.uv]] as const) {
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        if (gpu.mesh !== mesh) gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(location); gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gpu.indices);
      if (gpu.mesh !== mesh) gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.DYNAMIC_DRAW);
      gpu.mesh = mesh;
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, gpu.texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      gl.uniform2f(gpu.size, ctx.canvas.width, ctx.canvas.height); gl.uniform1i(gpu.source, 0);
      gl.drawElements(gl.TRIANGLES, mesh.indices.length, gl.UNSIGNED_SHORT, 0);
      const success = gl.getError() === gl.NO_ERROR;
      if (success) ctx.drawImage(gpu.canvas, 0, 0);
      if (isCanvasMemoryScopeActive()) {
        gpu.canvas.width = 1; gpu.canvas.height = 1;
        gl.bindTexture(gl.TEXTURE_2D, gpu.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      }
      if (success) { releaseScopedCanvas(source); return; }
    }
  }
  for (let i = 0; i < mesh.indices.length; i += 3) {
    const ids = [mesh.indices[i], mesh.indices[i + 1], mesh.indices[i + 2]];
    drawTexturedTriangle(ctx, source,
      ids.map(id => ({ x: mesh.uv[id * 2] * source.width, y: mesh.uv[id * 2 + 1] * source.height })) as [Point, Point, Point],
      ids.map(id => mesh.points[id]) as [Point, Point, Point], 0.35);
  }
  releaseScopedCanvas(source);
}
